import axios from 'axios';
import config from '../config';
import { invalidateForRequest } from '../lib/queryClient';
import { expireSession } from '../lib/sessionExpiry';

const API_BASE_URL = config.API_BASE_URL;

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',  // CSRF protection
    },
    withCredentials: true,  // Send HttpOnly cookies automatically
    timeout: 30000,
});

// Also set global defaults so raw axios calls (in services) send cookies
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Raw-axios services (auditService, riskService, …) bypass the `api` instance,
// so mirror the cache-invalidation hook on the GLOBAL axios too. Instances made
// with axios.create() do NOT inherit global interceptors, so this never
// double-fires for requests going through `api`.
axios.interceptors.response.use((response) => {
    invalidateForRequest(response.config?.method, response.config?.url);
    return response;
});

// X-Target-Tenant-ID comes ONLY from the page context (/t/<slug>), never from the request URL (Vik 2026-11-03
// 22:33 PT). The Global Console (/super-admin) calls /users/tenants/{slug}/... about a workspace without acting
// inside it; deriving the header from that URL made the server treat each save as a write inside the workspace
// and refuse it (support access is read-only).
export const pageTenantSlug = (pathname) => {
    const m = (pathname || '').match(/^\/t\/([^/]+)/);
    return m ? m[1] : null;
};

// Request interceptor — attach tenant context (no more localStorage token)
api.interceptors.request.use(
    (config) => {
        const tenantId = pageTenantSlug(window.location.pathname);
        if (tenantId) {
            config.headers['X-Target-Tenant-ID'] = tenantId;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ── Refresh-on-401 interceptor with queue pattern ────────────────────────────
// When access token expires, the first 401 triggers a /auth/refresh call.
// Subsequent 401s queue and retry after the refresh completes.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => {
        // Wave 1 (perf): central cache invalidation. Every successful mutating
        // request (POST/PUT/PATCH/DELETE) invalidates the React Query families
        // its URL touches (see queryClient.js INVALIDATION_RULES) — so cached
        // dashboard/controls data can never go stale after the user's own write.
        invalidateForRequest(response.config?.method, response.config?.url);
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Only intercept 401s (not 403s — those are permission errors)
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Don't retry refresh or login endpoints
        const url = originalRequest.url || '';
        if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout')) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // Queue this request to retry after refresh completes
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(() => {
                return api(originalRequest);
            }).catch((err) => {
                return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            // Call refresh endpoint (cookie-based — no body needed)
            await api.post('/auth/refresh');

            // Refresh succeeded — retry original request + all queued requests
            processQueue(null);
            return api(originalRequest);
        } catch (refreshError) {
            // Refresh failed — session is truly expired (#140): clear the session state and
            // replace the page with /login?msg=session-expired (once; public pages such as
            // /login, password setup and /vendor-assessment stay put, so no redirect loop).
            processQueue(refreshError);
            expireSession();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
